<template>
    <Head :title="'Expédition N°'+travel.reference" />

    <TravelLayout :currency="currency">
        <div class="grid grid-cols-5 gap-2 my-1">
            <div class="flex flex-col col-span-full sm:col-span-1">
                <!--Total colis -->
                <div class="p-4 mt-2 bg-white shadow-lg rounded-sm border border-gray-200">
                    <header class="flex justify-between items-center mb-2">
                        <div class="w-9 h-9 rounded-full flex justify-center items-center bg-gray-200 text-white mr-3">
                            <svg class="w5 h-5 fill-current" viewBox="0 0 24 24">
                                <path class="text-red-300" d="M12 10l12 7-12 7-12-7z"></path>
                                <path class="text-red-500" d="M12 5l12 7-12 7-12-7z"></path>
                                <path class="text-red-700" d="M12 0l12 7-12 7L0 7z"></path>
                            </svg>
                        </div>
                        <span v-if="stats.requestPending > 0" class="text-sky font-bold">
                            {{ stats.requestPending }} Demande{{ stats.requestPending > 1 ? 's' : '' }}
                        </span>
                    </header>
                    <h2 class="sm:text-sm xl:text-md font-semibold text-gray-400">Total colis</h2>
                    <div class="text-xl font-bold">{{ stats.totalPackage }} Colis</div>
                </div>

                <!--Total dépensé -->
                <div class="p-4 mt-2 bg-white shadow-lg rounded-sm border border-gray-200" v-if="$page.props.acl.isAdmin">
                    <header class="mb-2">
                        <div class="w-9 h-9 rounded-full flex justify-center items-center bg-red-500 text-white mr-3">
                            <svg class="w-9 h-9 fill-current text-white" viewBox="0 0 36 36">
                                <path d="M17.7 24.7l1.4-1.4-4.3-4.3H25v-2H14.8l4.3-4.3-1.4-1.4L11 18z"></path>
                            </svg>
                        </div>
                    </header>
                    <h2 class="sm:text-sm xl:text-md font-semibold text-gray-400">Total dépensé</h2>
                    <div class="text-xl font-bold">{{ formatAmount(stats.totalSpent, currency) }}</div>
                </div>

                <!--Total colis non payé -->
                <div class="p-4 mt-2 bg-white shadow-lg rounded-sm border border-gray-200" v-if="stats.showPackageStats">
                    <header class="mb-2">
                        <div class="w-9 h-9 rounded-full flex justify-center items-center bg-red-500 text-white mr-3">
                            <i class="fas fa-money-bill-wave text-lg"></i>
                        </div>
                    </header>
                    <h2 class="sm:text-sm xl:text-md font-semibold text-gray-400">Total colis non-payé</h2>
                    <div class="text-xl font-bold">{{ stats.totalPackageUnpaid }}</div>
                    <div class="text-gray-300 text-small text-red-400">#Colis partiellement payés.</div>
                </div>
            </div>
            <div class="flex flex-col col-span-full sm:col-span-1">
                <!--Total encaissé -->
                <div class="p-4 mt-2 bg-white shadow-lg rounded-sm border border-gray-200" v-if="$page.props.acl.isAdmin">
                    <header class="mb-2">
                        <div class="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-greendack-500">
                            <svg class="w-4 h-4 shrink-0 fill-current text-white" viewBox="0 0 16 16">
                                <path d="M15 4c.6 0 1 .4 1 1v10c0 .6-.4 1-1 1H3c-1.7 0-3-1.3-3-3V3c0-1.7 1.3-3 3-3h7c.6 0 1 .4 1 1v3h4zM2 3v1h7V2H3c-.6 0-1 .4-1 1zm12 11V6H2v7c0 .6.4 1 1 1h11zm-3-5h2v2h-2V9z"></path>
                            </svg>
                        </div>
                    </header>
                    <h2 class="sm:text-sm xl:text-md font-semibold text-gray-400">Total encaissé</h2>
                    <div class="text-xl font-bold">{{ formatAmount(stats.totalPaid, currency) }}</div>
                </div>

                <!--Total bébéfices -->
                <div class="p-4 mt-2 bg-white shadow-lg rounded-sm border border-gray-200" v-if="$page.props.acl.isAdmin">
                    <header class="flex justify-between items-center mb-2">
                        <div class="w-9 h-9 rounded-full flex items-center justify-center bg-greendack-500">
                            <svg class="h-7 w-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"></path>
                            </svg>
                        </div>
                        <span v-if="stats.totalPenalties > 0" class="text-red-400 font-bold">
                            Pénalités: {{ formatAmount(stats.totalPenalties, currency) }}
                        </span>
                    </header>
                    <h2 class="sm:text-sm xl:text-md font-semibold text-gray-400">Bénéfice</h2>
                    <div class="text-xl font-bold">{{ formatAmount(stats.totalProfit, currency) }}</div>
                </div>

                <!--Total colis void -->
                <div class="p-4 mt-2 bg-white shadow-lg rounded-sm border border-gray-200" v-if="stats.showPackageStats">
                    <header class="mb-2">
                        <div class="w-9 h-9 rounded-full flex justify-center items-center bg-violet-500 text-white">
                            <i class="fas fa-ban text-lg"></i>
                        </div>
                    </header>
                    <h2 class="sm:text-sm xl:text-md font-semibold text-gray-400">Total colis void</h2>
                    <div class="text-xl font-bold">{{ stats.totalPackageVoid }}</div>
                    <div class="text-gray-300 text-small text-violet-400">#Colis avec aucun paiement effectué.</div>
                </div>
            </div>
            <div class="flex flex-col col-span-full sm:col-span-1">
                <!--Total dù -->
                <div class="p-4 mt-2 bg-white shadow-lg rounded-sm border border-gray-200" v-if="$page.props.acl.isAdmin">
                    <header class="mb-2">
                        <div class="w-9 h-9 rounded-full flex items-center justify-center bg-yellow-500">
                            <svg class="w-8 h-8 fill-current text-white" viewBox="0 0 36 36">
                                <path d="M25 24H11a1 1 0 01-1-1v-5h2v4h12v-4h2v5a1 1 0 01-1 1zM14 13h8v2h-8z"></path>
                            </svg>
                        </div>
                    </header>
                    <h2 class="sm:text-sm xl:text-md font-semibold text-gray-400">Total dù</h2>
                    <div class="text-xl font-bold">{{ formatAmount(stats.totalDu, currency) }}  </div>
                </div>

                <!--Total articles -->
                <div class="p-4 mt-2 bg-white shadow-lg rounded-sm border border-gray-200">
                    <header class="flex justify-between items-center mb-2">
                        <div class="w-9 h-9 rounded-full flex items-center justify-center bg-gray-200">
                            <svg class="h-7 w-7" viewBox="0 0 20 20" fill="currentColor">
                                <path class="fill-current text-primaryOrange" fill-rule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4zm-6 3a1 1 0 112 0 1 1 0 01-2 0zm7-1a1 1 0 100 2 1 1 0 000-2z" clip-rule="evenodd"></path>
                            </svg>
                        </div>
                        <div class="font-bold text-greendack-500">{{ stats.totalItemPaid }} Vendu{{ stats.totalItemPaid > 1 ? 's' : '' }}</div>
                    </header>
                    <h2 class="sm:text-sm xl:text-md font-semibold text-gray-400">Total articles</h2>
                    <div class="text-xl font-bold">{{ stats.totalItem }} Article{{ stats.totalItem > 1 ? 's' : '' }}</div>
                </div>

                <!--Total colis abandonnés -->
                <div class="p-4 mt-2 bg-white shadow-lg rounded-sm border border-gray-200" v-if="stats.showPackageStats">
                    <header class="mb-2">
                        <div class="w-9 h-9 rounded-full flex justify-center items-center bg-gray-500 text-white mr-3">
                            <i class="fas fa-box-open text-lg"></i>
                        </div>
                    </header>
                    <h2 class="sm:text-sm xl:text-md font-semibold text-gray-400">Total colis abandonnés</h2>
                    <div class="text-xl font-bold">{{ stats.totalPackageAbandoned }}</div>
                    <div class="text-gray-300 text-small text-gray-400">#Colis déclarés comme abandonnés.</div>
                </div>
            </div>
            <!--Total Stats -->
            <div class="flex flex-col col-span-full sm:col-span-2" v-if="$page.props.acl.isAdmin">
                <div class="px-4 pt-4 mt-2 bg-white shadow-lg rounded-sm border border-gray-200">
                    <h2 class="font-semibold text-gray-400 text-center">Statistiques</h2>
                    <div class="grow flex flex-col justify-center">
                        <Doughnut :chart-data="chartData" :chart-options="chartOptions" :labels="labels" />
                    </div>
                </div>
            </div>
        </div>
    </TravelLayout>
</template>

<script>
    import { defineComponent } from 'vue'
    import { Doughnut } from 'vue-chartjs'
    import { Head } from '@inertiajs/inertia-vue3'
    import CurrencyMixins from '@/Mixins/CurrencyMixins'
    import TravelLayout from '@/Layouts/TravelLayout.vue'
    import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement, CategoryScale } from 'chart.js'

    // Import TailwindCSS variables
    import resolveConfig from 'tailwindcss/resolveConfig'
    import tailwindConfig from '../../../../tailwind.config'

    // Tailwind config
    const fullConfig = resolveConfig(tailwindConfig)

    ChartJS.register(Title, Tooltip, Legend, ArcElement, CategoryScale)

    export default defineComponent({
        components: {
            Head,
            Doughnut,
            TravelLayout
        },

        props: {
            stats: Object,
            travel: Object,
            currency: Object
        },

        mixins: [CurrencyMixins],

        setup(props) {
            const chartData = {
                labels: ['Dépenses', 'Encaissé', 'Restant'],
                datasets: [
                    {
                        data: [
                            props.stats.totalSpent,
                            props.stats.totalPaid,
                            props.stats.totalDu
                        ],
                        backgroundColor: [
                            fullConfig.theme.colors.red[500],
                            fullConfig.theme.colors.green[400],
                            fullConfig.theme.colors.yellow[500],
                        ],
                        hoverBackgroundColor: [
                            fullConfig.theme.colors.red[600],
                            fullConfig.theme.colors.green[500],
                            fullConfig.theme.colors.yellow[600],
                        ],
                        hoverBorderColor: fullConfig.theme.colors.white,
                    }
                ]
            }

            const chartOptions =  {
                cutout: '80%',
                layout: {
                    padding: 24
                },
                interaction: {
                    intersect: false,
                    mode: 'nearest'
                },
                animation: {
                    duration: 200,
                },
                maintainAspectRatio: false
            }

            return {
                chartData,
                chartOptions
            }
        },
    })
</script>
